import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BuyerSearchFilters } from "@/components/seller/search/BuyerSearchFilters";
import { BuyerSearchResults } from "@/components/seller/search/BuyerSearchResults";
import { AddToListDialog } from "@/components/seller/dialogs/AddToListDialog";
import { ContactBuyerDialog } from "@/components/seller/dialogs/ContactBuyerDialog";
import { useBuyerSearch } from "@/hooks/useBuyerSearch";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { buyerExportService } from "@/services/buyerExportService";
import { useToast } from "@/hooks/use-toast";
import { Filter, Users, List } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { safeStorage } from "@/utils/safariStorage";

const SearchBuyersPage = () => {
  console.log('SearchBuyersPage: Component mounting');
  usePageTitle("Search Buyers");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);
  const [selectedBuyerId, setSelectedBuyerId] = useState<string>('');
  const [selectedBuyerName, setSelectedBuyerName] = useState<string>('');
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'tile'>('tile');

  // Load saved view mode preference
  useEffect(() => {
    const savedView = safeStorage.getItem('buyerSearchViewMode');
    if (savedView === 'list' || savedView === 'tile') {
      setViewMode(savedView);
    }
  }, []);

  const handleViewModeChange = (mode: 'list' | 'tile') => {
    setViewMode(mode);
    safeStorage.setItem('buyerSearchViewMode', mode);
  };

  const {
    buyers,
    isLoading,
    totalCount,
    currentPage,
    totalPages,
    filters,
    setFilters,
    searchBuyers,
    resetFilters,
    goToPage
  } = useBuyerSearch();

  console.log('SearchBuyersPage: Hook data:', { 
    buyersCount: buyers?.length, 
    isLoading, 
    totalCount, 
    currentPage, 
    totalPages 
  });

  const handleFiltersChange = (newFilters: any) => {
    console.log('SearchBuyersPage: Filter change:', newFilters);
    setFilters(newFilters);
  };

  const handleSearch = () => {
    console.log('SearchBuyersPage: Searching with filters:', filters);
    searchBuyers(filters, 1);
  };

  const handleReset = () => {
    resetFilters();
  };


  const handleAddToList = (buyerId: string) => {
    const buyer = buyers.find(b => b.id === buyerId);
    if (buyer) {
      setSelectedBuyerId(buyerId);
      setSelectedBuyerName(buyer.full_name);
      setShowAddToListDialog(true);
    }
  };

  const [selectedBuyerContactInfo, setSelectedBuyerContactInfo] = useState<{email?: string | null; phone_number?: string | null}>();

  const handleContact = (buyerId: string) => {
    const buyer = buyers.find(b => b.id === buyerId);
    if (buyer) {
      setSelectedBuyerId(buyerId);
      setSelectedBuyerName(buyer.full_name);
      setSelectedBuyerContactInfo({
        email: buyer.email,
        phone_number: buyer.phone_number
      });
      setShowContactDialog(true);
    }
  };

  const handleExport = () => {
    if (buyers.length === 0) {
      toast({
        title: "No Data",
        description: "No buyers to export",
        variant: "destructive"
      });
      return;
    }

    try {
      buyerExportService.exportToCSV(buyers, `buyers-export-${new Date().toISOString().split('T')[0]}.csv`);
      toast({
        title: "Export Successful",
        description: `Exported ${buyers.length} buyers to CSV`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export buyers data",
        variant: "destructive"
      });
    }
  };

  console.log('SearchBuyersPage: Rendering component');

  return (
    <ErrorBoundary>
      <div className="py-6 space-y-6">
        {/* Page Header */}
        <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-6 w-6" />
                Search Buyers
              </CardTitle>
              <p className="text-muted-foreground mt-1">
                Find qualified investors for your deals
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
                size="sm"
              >
                <Filter className="h-4 w-4" />
                <span className="sm:inline">{showFilters ? 'Hide' : 'Show'} Filters</span>
              </Button>
              <Button
                onClick={() => navigate('/seller/buyer-lists')}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
                size="sm"
              >
                <List className="h-4 w-4" />
                <span className="sm:inline">My Lists</span>
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        {showFilters && (
          <div className="lg:col-span-1" data-tour="buyer-filters">
            <BuyerSearchFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onSearch={handleSearch}
              onReset={handleReset}
            />
          </div>
        )}

        {/* Results Section */}
        <div className={showFilters ? "lg:col-span-3" : "lg:col-span-4"} data-tour="buyer-results">
          {isLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Searching buyers...</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <BuyerSearchResults
              buyers={buyers}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              onAddToList={handleAddToList}
              onContact={handleContact}
              onExport={handleExport}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
            />
          )}
        </div>
      </div>

      {/* Dialogs */}
      <AddToListDialog
        open={showAddToListDialog}
        onOpenChange={setShowAddToListDialog}
        buyerId={selectedBuyerId}
        buyerName={selectedBuyerName}
      />

      <ContactBuyerDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
        buyerId={selectedBuyerId}
        buyerName={selectedBuyerName}
        buyerContactInfo={selectedBuyerContactInfo}
      />
    </div>
    </ErrorBoundary>
  );
};

export default SearchBuyersPage;